import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaChevronLeft, FaChevronRight, FaSyncAlt } from "react-icons/fa";
import { downloadCSV, downloadPDF, printThermal } from "../utils/exportHelpers";

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
};

const DEFAULT_REPORT = (date) => ({
  date,
  restaurant: "",
  total_orders: 0,
  total_quantity: 0,
  total_amount: 0,
  total_credit_amount: 0,
  modes: [],
});

const PaymentMethodReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [date, setDate] = useState(today());
  const [report, setReport] = useState(DEFAULT_REPORT(today()));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}").data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    if (userId) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, date]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    try {
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/report-v2/payment-methods",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: userId, date }),
        }
      );
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.message || "Failed to load report");
      setReport({ ...DEFAULT_REPORT(date), ...json, modes: Array.isArray(json?.modes) ? json.modes : [] });
    } catch (e) {
      toast.error(e.message || "Unable to load report");
      setReport(DEFAULT_REPORT(date));
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const changeDate = (delta) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    setDate(toISO(d));
  };

  const normalizedModes = useMemo(() => {
    const base = {
      cash: { label: "Cash" },
      card: { label: "Card" },
      upi: { label: "UPI" },
      credit: { label: "Credit" },
    };
    const incoming = report?.modes || [];
    const map = {};
    incoming.forEach((m) => {
      const key = String(m.mode || "").toLowerCase();
      map[key] = {
        key,
        label: m.mode || base[key]?.label || "Other",
        total_orders: Number(m.total_orders || 0),
        total_quantity: Number(m.total_quantity || 0),
        total_amount: Number(m.total_amount || 0),
        share: Number(m.share || 0),
      };
    });
    return Object.keys(base).map((key) => {
      const item = map[key] || { key, label: base[key].label, total_orders: 0, total_quantity: 0, total_amount: 0, share: 0 };
      return {
        ...item,
        avg_order_value: item.total_orders > 0 ? item.total_amount / item.total_orders : 0,
      };
    });
  }, [report]);

  const fmtINR = (n) =>
    `₹${Number(n || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;

  const exportCsv = () => {
    const rows = normalizedModes.map((it) => [
      it.label,
      it.total_orders,
      it.total_quantity,
      it.total_amount,
      `${it.share}%`,
      it.avg_order_value,
    ]);
    downloadCSV(
      `payment_method_report_${date}.csv`,
      ["Mode", "Orders", "Qty", "Amount", "Share", "Avg Order"],
      rows
    );
  };

  const exportPdf = () => downloadPDF(reportRef.current, `payment_method_report_${date}.pdf`);

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <Top>
        <div>
          <Title>Payment Method Report</Title>
          <Sub>Split of sales by payment method.</Sub>
          <PillRow>
            <Pill>Total Orders: {report.total_orders || 0}</Pill>
            <Pill>Total Qty: {report.total_quantity || 0}</Pill>
            <Pill>Total Amount: {fmtINR(report.total_amount)}</Pill>
            <Pill>Credit Amount: {fmtINR(report.total_credit_amount)}</Pill>
          </PillRow>
        </div>
        <Actions>
          <NavBtn onClick={() => changeDate(-1)}>
            <FaChevronLeft />
          </NavBtn>
          <DateInput>
            Date:
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </DateInput>
          <NavBtn onClick={() => changeDate(1)}>
            <FaChevronRight />
          </NavBtn>
          <PrimaryBtn onClick={() => fetchReport({ refresh: true })}>
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
          </PrimaryBtn>
        </Actions>
      </Top>

      <Card ref={reportRef}>
        {loading ? (
          <Center>Loading…</Center>
        ) : normalizedModes.length === 0 ? (
          <Center>No data for this date.</Center>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Mode</th>
                  <th>Orders</th>
                  <th>Qty</th>
                  <th>Amount</th>
                  <th>Share</th>
                  <th>Avg Order</th>
                </tr>
              </thead>
              <tbody>
                {normalizedModes.map((it) => (
                  <tr key={it.key}>
                    <td>{it.label}</td>
                    <td>{it.total_orders}</td>
                    <td>{it.total_quantity}</td>
                    <td>{fmtINR(it.total_amount)}</td>
                    <td>{Number(it.share || 0).toFixed(2)}%</td>
                    <td>{fmtINR(it.avg_order_value)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}

        <Actions style={{ marginTop: 10 }}>
          <PrimaryBtn onClick={exportPdf}>PDF</PrimaryBtn>
          <PrimaryBtn onClick={() => printThermal(reportRef.current, `payment_method_report_${date}`)}>
            Thermal Print
          </PrimaryBtn>
          <PrimaryBtn onClick={exportCsv}>CSV</PrimaryBtn>
        </Actions>
      </Card>
    </Page>
  );
};

export default PaymentMethodReportPage;

const today = () => toISO(new Date());
const toISO = (d) => d.toISOString().slice(0, 10);

const Page = styled.div`
  background: ${COLOR.bg};
  min-height: 100vh;
  padding: 16px;
`;
const Top = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;
const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color: ${COLOR.text};
`;
const Sub = styled.div`
  color: ${COLOR.muted};
`;
const PillRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`;
const Pill = styled.span`
  background: #eef2ff;
  color: ${COLOR.text};
  padding: 8px 10px;
  border-radius: 12px;
  font-weight: 800;
  border: 1px solid ${COLOR.line};
`;
const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;
const DateInput = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9f9f9;
  color: ${COLOR.text};
  input {
    border: 0;
    background: transparent;
    outline: none;
  }
`;
const NavBtn = styled.button`
  border: 1px solid ${COLOR.line};
  background: #fff;
  color: ${COLOR.primary};
  border-radius: 10px;
  padding: 8px 10px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`;
const PrimaryBtn = styled.button`
  background: ${COLOR.primary};
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;
const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 16px;
`;
const Center = styled.div`
  text-align: center;
  color: ${COLOR.muted};
  padding: 20px 0;
`;
const TableWrap = styled.div`
  overflow: auto;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 10px;
    border-bottom: 1px solid ${COLOR.line};
    text-align: left;
    font-size: 14px;
  }
  th {
    background: #f1f5f9;
    font-weight: 800;
  }
`;
